/* eslint-disable indent */
import React, { useEffect } from 'react';
import { FormData, FormErrors, IUpsertItemDialog } from './interfaces';
import {
  AppBar,
  Button,
  CustomTheme,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { SelectField } from '../../../../components/forms/SelectField';
import { atLeast, isEmpty } from '../../../../helpers/formHelpers';
import { LoadingLayover } from '../../../../components/LoadingLayover';
import { enqueueSnackbar } from 'notistack';
import {
  useGetAgentsList,
  useGetClientAgentsList,
} from '../../../../reactQuery/Agents/agents';
import { MutateOptions, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import useFormState from '../../../../hooks/useFormState';
import useTrackFormChanges from '../../../../hooks/useTrackFormChanges';
import {
  useGetAgentClientsList,
  useGetClientsList,
} from '../../../../reactQuery/clients';
import {
  useGetClientContactsList,
  useGetContactsList,
} from '../../../../reactQuery/contacts';
import { Form } from '../../../../components/forms';
import { useGetproductsList } from '../../../../reactQuery/products';
import {
  useCreateItemMutation,
  useGetStages,
  useUpdateItemMutation,
} from '../../../../reactQuery/opportunities';
import { useAuth } from '../../../../context/AuthProvider';

export const UpsertItemDialog: React.FC<IUpsertItemDialog> = ({
  item,
  show,
  onClose,
}): JSX.Element => {
  const { t } = useTranslation();
  const { auth } = useAuth();
  const queryClient = useQueryClient();

  const { mutate: createItem, isLoading: isCreatingItem } =
    useCreateItemMutation();
  const { mutate: updateItem, isLoading: isUpdatingItem } =
    useUpdateItemMutation();

  const theme = useTheme<CustomTheme>();
  const fullScreen = useMediaQuery(theme.breakpoints.down('tablet'));
  const isEditMode = !!item;
  const isAdmin = auth?.user && auth.user.isAdmin === true;

  const { formData, setFormFieldData, setFormData } = useFormState<FormData>(
    {}
  );
  const { formErrors, setFormErrors } = useTrackFormChanges<FormData>(formData);

  // const { data: agentsList, error: agentsListError } = useGetAgentsList(show);
  const {
    data: stagesAsList,
    error: stagesListError,
    isLoading: loadingStagesList,
  } = useGetStages();

  const { data: agentsList, error: agentsOfClientListError } =
    useGetClientAgentsList(formData.clientId, !!formData.clientId);

  const {
    data: clientsAsList,
    isLoading: loadingClientsAsList,
    error: clientsListAsError,
  } = useGetClientsList(isAdmin);

  const {
    data: clientsOfAgentList,
    isLoading: loadingClientsOfAgentList,
    error: clientsOfAgentListError,
  } = useGetAgentClientsList(auth?.user.id, !isAdmin);

  // const { data: contactsAsList, error: contactsListError } = useGetContactsList(
  //   !!item
  // );

  const { data: contactsList, error: contactsOfClientListError } =
    useGetClientContactsList(formData.clientId, !!formData.clientId);

  const {
    data: productsList,
    isLoading: loadingProductsList,
    error: ProductsListAsError,
  } = useGetproductsList();

  // const contactsList = isAdmin && item ? contactsAsList : contactsOfClientList;
  // const agentsList = isAdmin && item ? agentsAsList : agentsOfClientList;
  const clientsList = isAdmin ? clientsAsList : clientsOfAgentList;

  const isLoadingListOptions =
    (isAdmin && loadingClientsAsList) ||
    (!isAdmin && loadingClientsOfAgentList) ||
    loadingProductsList ||
    loadingStagesList;

  const validateForm = (): void => {
    const errors: FormErrors = {};

    if (!formData.title || isEmpty(formData.title)) {
      errors.title = t('errors.required');
    } else if (!atLeast(formData.title, 5)) {
      errors.title = t('errors.atLeast', {
        field: t('entities:opportunityItem.title').toLowerCase(),
        min: 5,
      });
    }

    if (formData.agentId === undefined) {
      errors.agentId = t('errors.pleaseSelectAnOption');
    }

    if (formData.contactId === undefined) {
      errors.contactId = t('errors.pleaseSelectAnOption');
    }

    if (formData.stageId === undefined) {
      errors.stageId = t('errors.pleaseSelectAnOption');
    }

    if (formData.clientId === undefined) {
      errors.clientId = t('errors.pleaseSelectAnOption');
    }

    if (formData.productId === undefined) {
      errors.productId = t('errors.pleaseSelectAnOption');
    }

    setFormErrors(errors);
  };

  const handleSubmit = async () => {
    if (!Object.keys(formErrors).length) {
      const itemData = {
        title: formData.title as string,
        stageId: formData.stageId as number,
        clientId: formData.clientId as number,
        agentId: formData.agentId as number,
        contactId: formData.contactId as number,
        productId: formData.productId as number,
      };

      const mutateOptions:
        | MutateOptions<number, AxiosError, unknown>
        | undefined = {
        onSuccess: (itemId) => {
          enqueueSnackbar(
            isEditMode
              ? t('forms:updateOpportunityItem.success')
              : t('forms:createOpportunityItem.success'),
            {
              variant: 'success',
            }
          );

          queryClient.invalidateQueries({
            queryKey: ['opportunity-stages', { includeItems: true }],
          });

          onClose();
        },
        onError: () => {
          enqueueSnackbar(
            isEditMode
              ? t('forms:updateOpportunityItem.failed')
              : t('forms:createOpportunityItem.failed'),
            {
              variant: 'error',
            }
          );
        },
      };

      if (isEditMode) {
        updateItem(
          {
            itemId: item.id,
            itemData,
          },
          mutateOptions
        );
      } else {
        createItem(itemData, mutateOptions);
      }
    }
  };

  const onClientChange = (clientId: number): void => {
    if (clientId !== formData.clientId) {
      setFormFieldData('agentId', undefined);
      setFormFieldData('contactId', undefined);
    }

    setFormFieldData('clientId', clientId);
  };

  useEffect(() => {
    const error =
      agentsOfClientListError ||
      clientsListAsError ||
      clientsOfAgentList ||
      contactsOfClientListError ||
      ProductsListAsError ||
      stagesListError;

    if (error) {
      enqueueSnackbar(t('errors.somethingWentWrong'), {
        variant: 'error',
      });

      onClose();
    }
  }, [
    agentsOfClientListError,
    clientsListAsError,
    clientsOfAgentListError,
    contactsOfClientListError,
  ]);

  useEffect(() => {
    if (agentsList && !isAdmin && auth?.user) {
      setFormFieldData('agentId', auth.user.id);
    }
  }, [agentsList]);

  useEffect(() => {
    setFormErrors({});

    if (item) {
      setFormData({
        title: item.title,
        clientId: item.client.id,
        contactId: item.contact.id,
        agentId: item.agent.id,
        productId: item.product.id,
        stageId: item.stage.id,
      });
    } else {
      setFormData({});
    }
  }, [show]);

  return (
    <Dialog
      fullScreen={fullScreen}
      open={show}
      keepMounted={false}
      //   TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <Typography sx={{ flex: 1 }} variant="h6" component="div">
            {isEditMode
              ? t('forms:updateOpportunityItem.title')
              : t('forms:createOpportunityItem.title')}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            disabled={isCreatingItem || isUpdatingItem}
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent
        dividers={true}
        sx={{
          minWidth: fullScreen ? undefined : '400px',
        }}
      >
        {isLoadingListOptions ? (
          <LoadingLayover height="200px" />
        ) : (
          <Form id="upsertItemForm" onSubmit={handleSubmit}>
            <TextField
              label={t('entities:opportunityItem.title')}
              variant="outlined"
              size="small"
              value={formData.title}
              placeholder={item?.title}
              onChange={(e) => setFormFieldData('title', e.target.value)}
              error={!!formErrors.title}
              helperText={formErrors.title}
              required
            />

            <SelectField
              name="product"
              label={t('entities:opportunityItem.product')}
              value={formData.productId ? String(formData.productId) : ''}
              onChange={(event: SelectChangeEvent) =>
                setFormFieldData('productId', Number(event.target.value))
              }
              error={!!formErrors.productId}
              errorMessage={formErrors.productId}
              disabled={!(productsList && productsList.length > 0)}
              required
            >
              {productsList?.map((product) => (
                <MenuItem key={product.id} value={product.id}>
                  {product.name}
                </MenuItem>
              ))}
            </SelectField>

            <SelectField
              name="stage"
              label={t('entities:opportunityItem.stage')}
              value={formData.stageId ? String(formData.stageId) : ''}
              onChange={(event: SelectChangeEvent) =>
                setFormFieldData('stageId', Number(event.target.value))
              }
              error={!!formErrors.stageId}
              errorMessage={formErrors.stageId}
              disabled={!(stagesAsList && stagesAsList.length > 0)}
              required
            >
              {stagesAsList?.map((stage) => (
                <MenuItem key={stage.id} value={stage.id}>
                  {stage.name}
                </MenuItem>
              ))}
            </SelectField>

            <SelectField
              name="client"
              label={t('entities:opportunityItem.client')}
              value={formData.clientId ? String(formData.clientId) : ''}
              onChange={(event: SelectChangeEvent) =>
                onClientChange(Number(event.target.value))
              }
              error={!!formErrors.clientId}
              errorMessage={formErrors.clientId}
              required
            >
              {clientsList?.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </SelectField>

            <SelectField
              name="contact"
              label={t('entities:opportunityItem.contact')}
              value={formData.contactId ? String(formData.contactId) : ''}
              onChange={(event: SelectChangeEvent) =>
                setFormFieldData('contactId', Number(event.target.value))
              }
              error={!!formErrors.contactId}
              errorMessage={formErrors.contactId}
              disabled={!(contactsList && contactsList.length > 0)}
              required
            >
              {contactsList?.map((contact) => (
                <MenuItem key={contact.id} value={contact.id}>
                  {`${contact.firstName} ${contact.lastName}`}
                </MenuItem>
              ))}
            </SelectField>

            <SelectField
              name="agent"
              label={t('entities:opportunityItem.agent')}
              value={formData.agentId ? String(formData.agentId) : ''}
              onChange={(event: SelectChangeEvent) =>
                setFormFieldData('agentId', Number(event.target.value))
              }
              error={!!formErrors.agentId}
              errorMessage={formErrors.agentId}
              disabled={!(agentsList && agentsList.length > 0) || !isAdmin}
              required
            >
              {agentsList?.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {`${agent.firstName} ${agent.lastName}`}
                </MenuItem>
              ))}
            </SelectField>
          </Form>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isCreatingItem || isUpdatingItem}>
          {t('buttons.cancel')}
        </Button>
        <Button
          form="upsertItemForm"
          type="submit"
          variant="contained"
          onClick={validateForm}
          disabled={isLoadingListOptions || isCreatingItem || isUpdatingItem}
        >
          {isEditMode ? t('buttons.update') : t('buttons.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
